import React, { useEffect, useState } from 'react';
import { Box, Spinner } from '@chakra-ui/react';
import { Outlet, useParams, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../supabaseClient';

function PhlebotomistLayout() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const checkAccess = async () => {
      try {
        // Check if the lab exists and get agreement status
        const { data, error } = await supabase
          .from('phlebotomist_profiles')
          .select('agreement_signed')
          .eq('id', id)
          .single();

        console.log('PhlebotomistLayout fetched:', { data, error, pathname: location.pathname });

        if (error) {
          console.error('Error fetching agreement status:', error);
          // If there's an error, don't block access - let the component handle it
          setLoading(false);
          return;
        }

        // If agreement is not signed and not on /agreement, redirect to agreement page
        // (We assume agreement is always required now)
        const needsAgreement = !data.agreement_signed;
        if (needsAgreement) {
          if (data.agreement_signed && location.pathname.endsWith('/agreement')) {
            console.log('Agreement signed, redirecting to dashboard');
            navigate(`/phlebotomist/${id}`, { replace: true });
            return;
          }
          if (!data.agreement_signed && !location.pathname.endsWith('/agreement')) {
            console.log('Agreement not signed, redirecting to agreement page');
            navigate(`/phlebotomist/${id}/agreement`, { replace: true });
            return;
          }
        }

        setLoading(false);
      } catch (err) {
        console.error('Error in checkAccess:', err);
        setError('Access denied');
        setLoading(false);
      }
    };

    // Add a small delay to ensure database updates have propagated
    const timeoutId = setTimeout(checkAccess, 100);

    return () => clearTimeout(timeoutId);
  }, [id, navigate, location.pathname]);

  // Log a GPS+time ping on open, then every 2 minutes while the portal stays
  // open, so admins get an accurate hours/location trail. Best-effort — records
  // the ping even if GPS is denied, and never blocks the portal.
  useEffect(() => {
    if (!id) return;
    const ping = () => {
      const save = (lat, lng, accuracy) => {
        supabase
          .from('phleb_portal_visits')
          .insert({ phlebotomist_id: id, lat, lng, accuracy, user_agent: navigator.userAgent })
          .then(() => {}, () => {});
      };
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => save(pos.coords.latitude, pos.coords.longitude, pos.coords.accuracy),
          () => save(null, null, null),
          { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 }
        );
      } else {
        save(null, null, null);
      }
    };
    ping();
    const iv = setInterval(ping, 2 * 60 * 1000);
    return () => clearInterval(iv);
  }, [id]);

  if (loading) {
    return (
      <Box minH="100vh" display="flex" alignItems="center" justifyContent="center">
        <Spinner size="xl" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box minH="100vh" display="flex" alignItems="center" justifyContent="center">
        <Box textAlign="center" color="red.500">
          {error}
        </Box>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg="gray.50">
      {/* Navigation removed for phlebotomist portal */}
      <Outlet context={{ labId: id }} />
    </Box>
  );
}

export default PhlebotomistLayout; 